import { isVisibleAuditEntry } from "../utils/auditLogScope";
import { registerStyles } from "../utils/registerStyles";
import React, { useEffect, useRef, useState } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { getFunctions, httpsCallable } from "firebase/functions";
import { FaHistory, FaSyncAlt } from "react-icons/fa";
import { useCompany } from "../contexts/CompanyContext";
import "./AdminPermissionsPage"; // Also registers the shared admin page styles.

const roles = { "super-admin": "Super-admin", admin: "Admin", user: "Användare", approved: "Godkänd", rejected: "Nekad", denied: "Nekad", pending: "Väntande", active: "Aktiv", invited: "Inbjuden" };
const actions = { created: "Skapad", updated: "Ändrad", deleted: "Borttagen" };
const localDate = (date) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
};
const isCreatedShift = (entry) => entry.category === "Pass" && entry.action === "created";
const entrySummary = (entry) => {
  if (!isCreatedShift(entry) || !entry.changes?.length) return entry.summary;
  const value = (...fields) => entry.changes.find((change) =>
    fields.includes(change.field) && change.after && !["–", "Ändrat värde"].includes(change.after))?.after;
  return ["Pass skapat", value("employeeName", "employee", "Anställd") || "Anställd saknas i loggen",
    value("projectName", "project", "Projekt") || "Projekt saknas i loggen",
    value("date", "Datum"), [value("startTime", "Starttid"), value("endTime", "Sluttid")].filter(Boolean).join("–")]
    .filter(Boolean).join(" · ");
};
const fieldLabels = { isRead: "Läst", visibleEmployeeIds: "Schemamedlemmar", approved: "Godkänd", startTime: "Starttid", endTime: "Sluttid", date: "Datum", startDate: "Startdatum", endDate: "Slutdatum", role: "Roll", status: "Status", membershipStatus: "Medlemsstatus", firstName: "Förnamn", lastName: "Efternamn", name: "Namn", absenceTypeName: "Frånvarotyp", employeeName: "Anställd", isPublished: "Publicerat", payroll: "Lön", timestamp: "Tidstämpling" };

const changeText = (change) => {
  const field = fieldLabels[change.field] || change.field;
  const display = (value) => roles[value] || (value === true || value === "true" ? "Ja" : value === false || value === "false" ? "Nej" : value);
  const before = display(change.before);
  const after = display(change.after);
  if (field === "Läst" && ["Ja", "Nej"].includes(after)) return after === "Ja" ? "Rapporten markerades som läst." : "Rapporten markerades som oläst.";
  if (field === "Godkänd" && ["Ja", "Nej"].includes(after)) return after === "Ja" ? "Godkändes." : "Godkännandet togs bort.";
  if (["Ja", "Nej"].includes(after)) return `${field}: ${after === "Ja" ? "aktiverades" : "avaktiverades"}.`;
  if ([before, after].includes("Ändrat värde")) return `${field} ändrades.`;
  if (!before || before === "–") return `${field}: ${after || "–"}.`;
  if (!after || after === "–") return `${field} togs bort (tidigare ${before}).`;
  return `${field} ändrades från ${before} till ${after}.`;
};
const readableChanges = (entry) => {
  if (isCreatedShift(entry)) return [];
  if (entry.category === "Rapport") return (entry.changes || []).filter(({ field }) => ["isRead", "Läst"].includes(field));
  return (entry.changes || []).filter((change) =>
    !["schemaId", "projectId", "employeeId", "year", "month", "time", "createdBy", "updatedBy"].includes(change.field));
};

export default function AdminLogPage() {
  const { activeCompany } = useCompany();
  return <CompanyLog key={activeCompany?.companyAccountUid} companyAccountUid={activeCompany?.companyAccountUid} companyName={activeCompany?.companyName} />;
}

function CompanyLog(props) {
  const [selectedDate, setSelectedDate] = useState(() => localDate(new Date()));
  return <DayLog key={selectedDate} {...props} selectedDate={selectedDate} setSelectedDate={setSelectedDate} />;
}

function DayLog({ companyAccountUid, companyName, selectedDate, setSelectedDate }) {
  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const mounted = useRef(true);
  const busy = useRef(false);

  const load = async (nextCursor = null) => {
    if (busy.current || !companyAccountUid) return;
    if (selectedDate > localDate(new Date())) {
      setEntries([]);
      setCursor(null);
      setError("");
      setLoading(false);
      return;
    }
    busy.current = true;
    setLoading(true);
    setError("");
    try {
      const dayStart = new Date(`${selectedDate}T00:00:00`);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      const getLog = httpsCallable(getFunctions(), "getCompanyAuditLog");
      let pageCursor = nextCursor;
      let dayEntries = [];
      let hasMoreForDay = false;
      do {
        const { data } = await getLog({ companyAccountUid, cursor: pageCursor,
          dayStart: dayStart.toISOString(),
          dayEnd: dayEnd.toISOString(),
        });
        if (!mounted.current) return;
        // Äldre serverversioner kan skicka andra dagar; fortsätt tills vald dag nås.
        dayEntries = data.entries.filter((entry) => localDate(entry.occurredAt) === selectedDate && isVisibleAuditEntry(entry));
        const lastDate = localDate(data.entries[data.entries.length - 1]?.occurredAt);
        hasMoreForDay = Boolean(data.cursor && (!data.entries.length || lastDate >= selectedDate));
        pageCursor = hasMoreForDay ? data.cursor : null;
      } while (!dayEntries.length && pageCursor);
      if (!mounted.current) return;
      // Loggdagen är dagen då åtgärden utfördes, aldrig passets datum.
      setEntries((previous) => nextCursor ? [...previous, ...dayEntries.filter((entry) => !previous.some((old) => old.id === entry.id))] : dayEntries);
      setCursor(hasMoreForDay ? pageCursor : null);
    } catch (error) {
      if (mounted.current) setError("Kunde inte hämta loggen. Försök igen.");
    } finally {
      busy.current = false;
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
    // Företagsbyte monterar en ny logg med eget tillstånd.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = entries.filter((entry) =>
    ["admin", "super-admin"].includes(entry.actor?.role) &&
    (entry.actor.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  if (loading && entries.length === 0) {
    return <LoadingIndicator message="Laddar loggen..." fullPage />;
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      <div style={{ marginBottom: 16, padding: "0 0 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar logg för: <strong>{companyName || "Inget företag valt"}</strong>
        </p>
      </div>
    <section className="admin-permissions admin-permissions-page" aria-labelledby="admin-log-title">
      <header className="admin-permissions__header">
        <div className="admin-permissions__heading-icon"><FaHistory aria-hidden="true" /></div>
        <div className="admin-permissions__heading">
          <span className="admin-permissions__eyebrow">SUPER-ADMIN</span>
          <h1 id="admin-log-title">Logg</h1>
          <p>Se vem som ändrade vad och när i företaget.</p>
        </div>
        <button className="admin-permissions__secondary audit-refresh" disabled={loading} onClick={() => load()}><FaSyncAlt aria-hidden="true" /> Uppdatera</button>
      </header>
      <div className="admin-permissions__content">
        <div className="admin-permissions__notice">Här visas ändringar i pass, löner, användare och schemamedlemskap, godkända stämplingstider, regler, övertids- och frånvaroärenden samt rapporters lässtatus. Nya händelser kan ta några sekunder att visas.</div>
        <div className="audit-toolbar">
          <div><h2 className="audit-toolbar-title">Händelser</h2><p className="audit-toolbar-count">{filtered.length} av {entries.length} inlästa händelser för {selectedDate}</p></div>
          <div className="audit-filters">
            <input type="search" aria-label="Sök administratörens namn" placeholder="Sök administratörens namn…" value={search} onChange={(event) => setSearch(event.target.value)} />
            <input type="date" aria-label="Visa logg för datum" value={selectedDate} onChange={(event) => { if (event.target.value) setSelectedDate(event.target.value); }} />
          </div>
        </div>
        {error && <p role="alert" className="admin-permissions__error">{error}</p>}
        {loading && <LoadingIndicator message="Laddar loggen..." />}
        {!loading && !error && !filtered.length && <div className="admin-permissions__empty">{entries.length ? "Inga händelser matchar dina filter." : "Inga händelser har registrerats för valt datum."}</div>}
        <div className="audit-list">{filtered.map((entry) => (
          <article className="admin-permissions__card" key={entry.id}>
            <header className="audit-entry-header">
              <div className="audit-author">
                <h2 className="audit-author-name">Ändring av: <strong>{entry.actor?.name || "Okänd användare"}</strong></h2>
              </div>
              <time className="audit-time" dateTime={entry.occurredAt}>{new Date(entry.occurredAt).toLocaleString("sv-SE", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })}</time>
            </header>
            <div className="audit-description">
              <span className={`audit-badge audit-${entry.action}`}>{actions[entry.action] || entry.action}</span>
              <p className="audit-summary">{entrySummary(entry).replace(/^Pass skapad/, "Pass skapat").replace(/^Pass ändrad/, "Pass ändrat").replace(/^Pass borttagen/, "Pass borttaget").split(" · ").filter((part) => part.trim()).join(" · ")}</p>
              {readableChanges(entry).length > 0 && <ul className="audit-changes">{readableChanges(entry).map((change, index) => <li key={index}>{changeText(change)}</li>)}</ul>}
            </div>
          </article>
        ))}</div>
        {cursor && <button className="admin-permissions__secondary audit-more" disabled={loading} onClick={() => load(cursor)}>Ladda fler händelser</button>}
      </div>
    </section>
    </div>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("AdminLogPage", `
.audit-refresh, .audit-more { padding: 10px 14px; border-radius: 9px; font-size: 12px !important; }
.audit-filters { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
.audit-filters select { padding: 10px; border: 1px solid #e4e1ec; border-radius: 9px; background: white; color: #62556f; }
.audit-count, .audit-person { font-size: 12px; color: #82758e; }
.audit-list { display: grid; gap: 14px; }
.audit-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.audit-row time { color: #82758e; font-size: 12px; }
.audit-badge { padding: 4px 9px; border-radius: 6px; font-size: 11px; background: #eee7fa; color: #7042a9; }
.audit-created { background: #e7f5ee; color: #26704c; }
.audit-deleted { background: #ffebee; color: #ad344c; }
.audit-summary { font-size: 15px !important; margin: 14px 0 8px !important; overflow-wrap: anywhere; }
.audit-list summary { font-size: 12px; cursor: pointer; color: #7c3aed; }
.audit-table { overflow-x: auto; }
.audit-table table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 12px; }
.audit-table th, .audit-table td { text-align: left; padding: 10px; border-bottom: 1px solid #eee8f4; overflow-wrap: anywhere; }
.audit-more { display: block; margin: 20px auto 0; }

.audit-entry-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; padding-bottom: 14px; border-bottom: 1px solid #eee8f4; }
.audit-author { display: grid; gap: 7px; min-width: 0; }
.audit-author-name { margin: 0 !important; font-size: 15px !important; font-weight: 400; color: #44364f; overflow-wrap: anywhere; }
.audit-time { font-size: 12px; color: #82758e; }
.audit-description { padding-top: 16px; }
.audit-changes { margin: 10px 0 0; padding-left: 20px; color: #62556f; font-size: 13px; line-height: 1.7; overflow-wrap: anywhere; }
.audit-changes li + li { margin-top: 4px; }

.audit-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 18px 0; margin-bottom: 18px; border-bottom: 1px solid #ececf4; }
.audit-toolbar-title { margin: 0 !important; font-size: 17px !important; font-weight: 800; color: #252b42; }
.audit-toolbar-count { color: #8891a8; font-size: 13px; margin: 4px 0 0; }
.audit-toolbar .audit-filters { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 0; }
.audit-toolbar .audit-filters input, .audit-toolbar .audit-filters select { box-sizing: border-box; min-height: 42px; min-width: 0; max-width: 100%; padding: 0 12px; border: 1px solid #ececf4; border-radius: 12px; background: #fff; color: #252b42; font-size: 14px; font-family: inherit; }
.audit-toolbar .audit-filters :focus-visible { outline: 2px solid #6323ef; outline-offset: 2px; }
`);
